import {Component, Inject, Input} from '@angular/core';
import {UntypedFormArray} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';

import {CardsApiService, filePath, SwalService} from '@atmi/core';
import {Relation, RelationOAMeta} from '@atmi/core/models';
import {CardsDialogComponent} from '@atmi/shared/cards-dialog/cards-dialog.component';
import {TranslateService} from '@ngx-translate/core';

import {BaseCtrlValueAccessorFormArrayComponent, FormManagerService} from '@sim/form';
import {ClassName} from '@sim/role';

import {BehaviorSubject, combineLatest, firstValueFrom, forkJoin} from 'rxjs';
import {filter, first, map, switchMap, takeUntil} from 'rxjs/operators';

type RelationOA = Relation & { beneOA: string };

@Component({
  selector: 'app-cards-oa-manager',
  styleUrls: ['./cards-oa-manager.component.scss'],
  templateUrl: './cards-oa-manager.component.html',
})
export class CardsOaManagerComponent extends BaseCtrlValueAccessorFormArrayComponent<RelationOA> {

  @Input() classNames: ClassName[] = [];

  private readonly cardId$ = new BehaviorSubject<string | undefined>(undefined);

  @Input() set cardId(v: string | undefined) {
    this.cardId$.next(v);
  }

  get cardId() {
    return this.cardId$.value;
  }

  readonly relationsCards$ = combineLatest([
    this.cardId$.pipe(),
    this.formValue$.pipe(filter((form): form is UntypedFormArray => !!form)),
  ]).pipe(
    switchMap(([_, form]) => {
      const cardsIds: string[] = form.value.map((r: RelationOA) => r.beneOA);
      const cardsPromises = cardsIds.map((id) => firstValueFrom(this.cardsApi.getOA(id).pipe(first())));

      return cardsPromises.length > 0
        ? forkJoin(cardsPromises)
        : Promise.resolve([]);
    }),
    map((cards) => {
      if (!this.form) {
        throw new Error('Cannot set cards list without a form');
      }

      const cardsRelations: RelationOA[] = this.form.value;
      return cardsRelations.map((card) => {
        const linked = cards.find((c) => c.id === card.beneOA);
        return {
          ...card,
          bene: {
            ...linked,
            previewUrl: linked && linked.previewUrl ? this.fileUrl + linked.previewUrl : undefined,
          },
        };
      });
    }),
    takeUntil(this.unsubscription$),
  );

  constructor(
    private readonly cardsApi: CardsApiService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly swal: SwalService,
    private readonly formManager: FormManagerService,
    @Inject(filePath) private readonly fileUrl: string,
  ) {
    super();
  }

  async addRelationHandler() {

    const ref = this.dialog.open(CardsDialogComponent, {data: {type: 'oa'}, width: '1000px'});
    const res: RelationOA | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (res && this.form) {
      const group = this.formManager.makeForm(RelationOAMeta, {...res});

      this.form.push(group);

      this.value = [...this.form.value];
    }

  }

  // tslint:disable-next-line:variable-name
  async removeRelationHandler(_: RelationOA, index: number) {
    const res = await this.swal.showConfirm({
      text: this.translate.instant('Sei sicuro di voler rimuovere questa relazione?'),
      title: this.translate.instant('Rimozione relazione'),
    });

    if (res && this.form) {
      this.form.removeAt(index);
      this.value = [...this.form.value];
    }
  }
}
